import { useSearchParams } from "react-router-dom";
import "../../css/diagnostic/listall.css";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import MobileHeader from "../../component/MobileHeader";

export default function ListAll() {
    const [searchParams] = useSearchParams();

    const fldIdx = searchParams.get("fldIdx");
    const targetDate = searchParams.get("targetDate");
    const dateType = searchParams.get("dateType") || "inspection";

    const API = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("accessToken");

    const [count, setCount] = useState(0);
    const [expandedDetail, setExpandedDetail] = useState(new Set());
    const [dataList, setDataList] = useState([]);

    const isOccurrence = dateType === "occurrence";
    const pageTitle = isOccurrence ? "발생 내역" : "점검 내역";
    const pageSubTitle = isOccurrence
        ? "선택한 날짜의 발생 내역을 확인할 수 있습니다."
        : "선택한 날짜의 등록 내역을 확인할 수 있습니다.";

    const primaryDateLabel = isOccurrence ? "발생 일시" : "점검 일시";
    const secondaryDateLabel = isOccurrence ? "점검 일시" : "발생 일시";

    const toggleDetail = (idx) => {
        setExpandedDetail((prev) => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };

    const formatDateTime = (value) => {
        if (!value) return "-";

        const str = String(value).replace("T", " ");
        return str.length >= 16 ? str.slice(0, 16) : str;
    };

    const getPrimaryDateValue = (data) => {
        return isOccurrence ? (data.caughtDT || "-") : (data.createdAt || "-");
    };

    const getSecondaryDateValue = (data) => {
        return isOccurrence ? (data.createdAt || "-") : (data.caughtDT || "-");
    };

    useEffect(() => {
        if (!fldIdx || !targetDate) {
            setDataList([]);
            return;
        }

        axios
            .get(`${API}/list`, {
                params: {
                    fldIdx,
                    targetDate,
                    dateType,
                },
                headers: {
                    "Content-type": "application/json",
                    Authorization: token,
                },
            })
            .then((resp) => {
                setDataList(Array.isArray(resp.data) ? resp.data : []);
            })
            .catch((e) => {
                console.error(e);
                setDataList([]);
            });
    }, [API, fldIdx, targetDate, dateType, token]);

    useEffect(() => {
        if (!fldIdx || !targetDate) {
            setCount(0);
            return;
        }

        axios
            .get(`${API}/count`, {
                params: {
                    fldIdx,
                    targetDate,
                    dateType,
                },
                headers: {
                    "Content-type": "application/json",
                    Authorization: token,
                },
            })
            .then((resp) => {
                setCount(resp.data || 0);
            })
            .catch((err) => {
                console.error(err);
                setCount(0);
            });
    }, [API, fldIdx, targetDate, dateType, token]);

    const datalist = useMemo(() => {
        return dataList.map((data, idx) => {
            const isDetailExpanded = expandedDetail.has(idx);

            const note = data.caughtNote || "";
            const shortNote = note.length > 90 ? `${note.slice(0, 90)}...` : note;

            const actionDesc = data.actionDescription || "";
            const shortAction =
                actionDesc.length > 90 ? `${actionDesc.slice(0, 90)}...` : actionDesc;

            const minusPoint = Number(data.totalDeductedPT ?? 0);
            const plusPoint = Number(data.totalIncreasePT ?? 0);
            const hasMinus = minusPoint > 0;
            const hasPlus = plusPoint > 0;

            const primaryDateValue = getPrimaryDateValue(data);
            const secondaryDateValue = getSecondaryDateValue(data);

            return (
                <article
                    className="la_item"
                    key={data.diagIdx || `${primaryDateValue || targetDate}-${idx}`}
                >
                    <div className="la_item_top">
                        <div className="la_person_group">
                            <strong className="la_person_name">
                                {data.subcontractorEngnrNM || "-"}
                            </strong>
                            <span className="la_company_name">
                                {data.subcontractorNM || "-"}
                            </span>
                        </div>

                        <div className="la_date_badge">
                            일시 : {formatDateTime(data.caughtDT)}
                        </div>
                    </div>

                    <div className="la_date_row">
                        <div className="la_date_badge">
                            점검 일시 : {formatDateTime(data.createdAt)}
                        </div>
                    </div>

                    <div className="la_info_box">
                        <strong>작업지휘자</strong>
                        <span>{data.supervisorName || "-"}</span>
                    </div>

                    <div className="la_info_box full">
                        <strong>점검 라인</strong>
                        <span>{data.lineName || "-"}</span>
                    </div>

                    <div className="la_info_box full">
                        <strong>위치</strong>
                        <span>{data.caughtPlace || "-"}</span>
                    </div>

                    <div className="la_info_box">
                        <strong>설비명</strong>
                        <span>{data.equipmentName || "-"}</span>
                    </div>

                    <div className="la_info_grid">
                        <div className="la_info_box">
                            <strong>분류</strong>
                            <span>{data.evaluation?.evlNM || "-"}</span>
                        </div>

                        <div className="la_info_box">
                            <strong>내용</strong>
                            <span>{data.evaluationClass?.classNM || "-"}</span>
                        </div>

                        <div className="la_info_box full">
                            <strong>세부사항</strong>
                            <span>{data.evaluationCategory?.categoryNM || "-"}</span>
                        </div>
                    </div>

                    {(hasMinus || hasPlus) && (
                        <div className="la_score_row single">
                            {hasMinus && (
                                <div className="la_score_card minus_only">
                                    <strong>감점</strong>
                                    <span className="minus">-{minusPoint}</span>
                                </div>
                            )}

                            {hasPlus && (
                                <div className="la_score_card plus_only">
                                    <strong>가점</strong>
                                    <span className="plus">+{plusPoint}</span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="la_note_section">
                        <div className="la_note_head">
                            <strong>상세내용</strong>
                            {note.length > 90 && (
                                <button
                                    type="button"
                                    className="la_expand_btn"
                                    onClick={() => toggleDetail(idx)}
                                    aria-expanded={isDetailExpanded}
                                    aria-controls={`detail-${idx}`}
                                >
                                    {isDetailExpanded ? "접기" : "펼쳐보기"}
                                </button>
                            )}
                        </div>

                        <p
                            id={`detail-${idx}`}
                            className={`la_note_text ${isDetailExpanded ? "expanded" : ""}`}
                        >
                            {isDetailExpanded ? note : shortNote || "-"}
                        </p>
                    </div>

                    <div className="la_note_section">
                        <div className="la_note_head">
                            <strong>조치내용</strong>
                        </div>
                        <p className="la_note_text">{shortAction || "-"}</p>
                    </div>

                    <div className="la_bottom_row">
                        <div className="la_inspector">
                            <strong>점검자</strong>
                            <span>{data.regNM || "-"}</span>
                        </div>
                    </div>
                </article>
            );
        });
    }, [dataList, expandedDetail, targetDate, isOccurrence]);

    return (
        <div className="page">
            <div className="list_mobile">
                <MobileHeader onPrev={() => window.history.back()} />

                <main className="main">
                    <h1 className="title">점검 내역</h1>
                    <p className="sub_title">{pageSubTitle}</p>

                    <section className="la_summary_card">
                        <div className="la_summary_top">
                            <div className="la_date_chip">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5z" />
                                </svg>
                                <span>{primaryDateLabel} : {targetDate || "-"}</span>
                            </div>

                            <div className="la_count_text">
                                전체 <strong>{count}</strong>건
                            </div>
                        </div>
                    </section>

                    <section className="la_list_wrap">
                        {dataList.length > 0 ? (
                            <div className="la_list">{datalist}</div>
                        ) : (
                            <div className="la_empty">
                                <div className="la_empty_icon">📋</div>
                                <p>등록된 내역이 없습니다.</p>
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
}